import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState, useMemo } from "react";
import { Shield, Gauge, ClockClockwise, UsersThree, MapPinLine, Pulse as Activity, SignOut, Broadcast, ChatCircleText, BellSimple, X, PaperPlaneTilt, Repeat } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/dashboard", label: "Dispatch",  icon: Gauge, testid: "nav-dashboard" },
  { to: "/shifts",    label: "Shifts",    icon: ClockClockwise, testid: "nav-shifts" },
  { to: "/templates", label: "Templates", icon: Repeat, testid: "nav-templates" },
  { to: "/guards",    label: "Guards",    icon: UsersThree, testid: "nav-guards" },
  { to: "/sites",     label: "Sites",     icon: MapPinLine, testid: "nav-sites" },
  { to: "/activity",  label: "Activity",  icon: Activity, testid: "nav-activity" },
];

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="font-mono text-xs text-zinc-400 tabular-nums" data-testid="header-clock">
      {now.toUTCString().split(" ").slice(1, 5).join(" ")}
    </div>
  );
};

const Layout = () => {
  const { user, loading, logout, refresh } = useAuth();
  const location = useLocation();
  const [config, setConfig] = useState({ mocked_sms: true });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [alertPhone, setAlertPhone] = useState("");
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const sendTestSms = async () => {
    if (!alertPhone) {
      toast.error("Save your phone number first, then test.");
      return;
    }
    setTesting(true);
    try {
      // Save the phone first so the backend has it when we trigger /sms/test
      await api.patch("/auth/me", { alert_phone: alertPhone, alerts_enabled: alertsEnabled });
      const { data } = await api.post("/sms/test");
      if (data.mocked) {
        toast.message("Test SMS logged (MOCK mode)", {
          description: "Drop Twilio creds in backend/.env to send for real.",
        });
      } else if (data.ok) {
        toast.success(`Test SMS sent to ${data.phone}`);
      } else {
        // Show the actual Twilio error so the dispatcher can fix it
        toast.error("Twilio rejected the message", {
          description: data.error || "Unknown error",
          duration: 10000,
        });
      }
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    const id = setTimeout(() => {
      api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    }, 0);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const id = setTimeout(() => {
      setAlertPhone(user.alert_phone || "");
      setAlertsEnabled(user.alerts_enabled !== false);
    }, 0);
    return () => clearTimeout(id);
  }, [user]);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/auth/me", { alert_phone: alertPhone, alerts_enabled: alertsEnabled });
      toast.success(alertsEnabled && alertPhone ? "Sandy will text you on escalations" : "Alerts updated");
      await refresh();
      setSettingsOpen(false);
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const navState = useMemo(() => ({ from: location.pathname }), [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500 font-mono text-xs uppercase tracking-[0.3em]">
        Authenticating...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={navState} replace />;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] bg-grid text-white flex">
      {/* Sidebar */}
      <aside className="w-60 border-r border-white/10 flex flex-col bg-[#0A0A0A] sticky top-0 h-screen">
        <div className="px-5 pt-6 pb-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 grid place-items-center bg-[#007AFF] text-white">
              <Shield size={18} weight="fill" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-black tracking-tight text-base">SANDY</div>
              <div className="label-mono text-[0.6rem]">Automated Dispatch</div>
            </div>
          </div>
        </div>

        <nav className="px-3 py-4 flex flex-col gap-0.5 flex-1">
          <div className="label-mono px-2 py-2">Operations</div>
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm border-l-2 ${
                  isActive
                    ? "bg-white/5 border-[#007AFF] text-white"
                    : "border-transparent text-zinc-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <n.icon size={18} weight="duotone" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-8 h-8 grid place-items-center bg-white/10 text-white font-mono text-xs uppercase">
              {(user.name || user.email).slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate" data-testid="user-name">{user.name}</div>
              <div className="text-[0.65rem] text-zinc-500 truncate font-mono">{user.email}</div>
            </div>
          </div>
          <button
            data-testid="alerts-settings-btn"
            onClick={() => setSettingsOpen(true)}
            className="w-full mt-2 flex items-center justify-between gap-2 px-3 py-2 text-xs uppercase tracking-wider text-zinc-400 hover:bg-white/5 hover:text-white border border-white/10"
          >
            <span className="flex items-center gap-2"><BellSimple size={14} /> My Alerts</span>
            <span className={`w-1.5 h-1.5 rounded-full ${user.alerts_enabled && user.alert_phone ? "bg-emerald-500" : "bg-zinc-600"}`} />
          </button>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full mt-2 flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider text-zinc-400 hover:bg-white/5 hover:text-white border border-white/10"
          >
            <SignOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Settings modal */}
      {settingsOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="settings-modal">
          <div className="bg-[#121212] border border-white/10 max-w-md w-full">
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
              <div className="label-mono">// MY ESCALATION ALERTS</div>
              <button onClick={() => setSettingsOpen(false)} className="text-zinc-500 hover:text-white"><X size={16}/></button>
            </div>
            <form onSubmit={saveSettings} className="p-5 space-y-4">
              <p className="text-sm text-zinc-400">
                When a guard goes <span className="text-yellow-400">LATE</span>, <span className="text-red-400">NO-SHOW</span>, or texts back that they can&apos;t make it, Sandy will text you immediately at this number.
              </p>
              <div>
                <label className="label-mono block mb-1">Your SMS Number</label>
                <input
                  data-testid="alert-phone-input"
                  type="tel"
                  value={alertPhone}
                  onChange={(e) => setAlertPhone(e.target.value)}
                  placeholder="+15555550100"
                  className="w-full bg-[#0A0A0A] border border-white/10 focus:border-[#007AFF] outline-none px-3 py-2 text-sm font-mono"
                />
                <p className="text-[0.65rem] text-zinc-500 mt-1 font-mono">E.164 format, e.g. +1 followed by 10 digits.</p>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  data-testid="alerts-enabled-toggle"
                  type="checkbox"
                  checked={alertsEnabled}
                  onChange={(e) => setAlertsEnabled(e.target.checked)}
                  className="w-4 h-4 accent-[#007AFF]"
                />
                <span className="text-sm">Text me on every escalation</span>
              </label>
              <div className="flex justify-between items-center gap-2 pt-2 border-t border-white/5 mt-3 pt-3">
                <button
                  type="button"
                  data-testid="send-test-sms-btn"
                  onClick={sendTestSms}
                  disabled={testing || !alertPhone}
                  className="flex items-center gap-2 px-3 py-2 border border-[#2BD4D9]/40 text-[#2BD4D9] hover:bg-[#2BD4D9]/10 text-xs uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <PaperPlaneTilt size={12} weight="bold" />
                  {testing ? "Sending..." : "Send Test SMS"}
                </button>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setSettingsOpen(false)} className="px-3 py-2 border border-white/15 text-xs uppercase tracking-wider">Cancel</button>
                  <button data-testid="save-alerts-btn" type="submit" disabled={saving} className="px-3 py-2 bg-[#007AFF] hover:bg-[#0066d6] text-xs uppercase tracking-wider font-semibold disabled:opacity-50">
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-white/10 bg-[#0A0A0A] flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 ${config.mocked_sms ? "bg-yellow-500 pulse-warn" : "bg-emerald-500"}`} />
              <span className="label-mono" data-testid="sms-status">
                {config.mocked_sms ? "MOCK SMS" : "SMS LIVE"}
              </span>
            </div>
            <div className="text-zinc-700">|</div>
            <ChatCircleText size={14} className="text-zinc-500" />
            <span className="label-mono">
              {config.twilio_phone ? `TWILIO · ${config.twilio_phone}` : "DISPATCH CHANNEL"}
            </span>
          </div>
          <Clock />
        </header>

        <main className="flex-1 p-6 lg:p-8 panel-in">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
